import React from 'react';

import { Box } from '@mui/material';

import { ActivityLogsList } from 'src/components/activityLog';
import { TestVerificationRequestContent } from 'src/components/testVerificationRequest';
import { VerificationRequestStatusBar } from 'src/components/verificationRequest';
import { InboxItemTypeWithResources } from 'src/constants/inbox';
import { useInboxItemById } from 'src/hooks/inboxItem';
import {
  useTestVerificationRequest,
  useTestVerificationRequestsRoute,
} from 'src/hooks/testVerificationRequests';
import { useVerificationRequest } from 'src/hooks/verificationRequest';

import { InboxItemContentApiVerificationRequest } from '../InboxItemContentApiVerificationRequest';
import { InboxItemContentDataDisputeCVStaff } from '../InboxItemContentDataDispute';
import {
  InboxItemContentVerificationRequestCreateCommentForm,
  InboxItemContentVerificationRequestCVStaff,
  InboxItemContentVerificationRequestPaymentStatus,
} from '../InboxItemContentVerificationRequest';
import { InboxItemContentWrapper } from '../InboxItemContentWrapper';
import { InboxItemPlatformStaffMenu } from '../InboxItemPlatformStaffMenu';

type InboxItemContentPlatformStaffProps = {
  inboxItemId: string | undefined;
};

export const InboxItemContentPlatformStaff: React.FC<InboxItemContentPlatformStaffProps> = ({
  inboxItemId,
}) => {
  const { inboxItem } = useInboxItemById(inboxItemId);

  const { verificationRequest } = useVerificationRequest({ inboxItemId });

  const { isTestRequestsRoute } = useTestVerificationRequestsRoute();
  const { testVerificationRequest, loading: testVerificationRequestLoading } =
    useTestVerificationRequest(inboxItemId, {
      skip: !isTestRequestsRoute,
    });

  const inboxItemContent = React.useMemo(() => {
    switch (inboxItem?.type) {
      case InboxItemTypeWithResources.VerificationRequest:
        return <InboxItemContentVerificationRequestCVStaff />;

      case InboxItemTypeWithResources.DataDispute:
        return <InboxItemContentDataDisputeCVStaff />;
      case InboxItemTypeWithResources.ApiVerificationRequest:
        return <InboxItemContentApiVerificationRequest />;
      default:
        return null;
    }
  }, [inboxItem?.type]);

  if (inboxItemId && isTestRequestsRoute && testVerificationRequest) {
    return (
      <TestVerificationRequestContent
        inboxItemId={inboxItemId}
        testVerificationRequest={testVerificationRequest}
        loading={testVerificationRequestLoading}
      />
    );
  }

  return (
    <InboxItemContentWrapper
      statusBar={
        inboxItemId &&
        inboxItem?.type === InboxItemTypeWithResources.VerificationRequest && (
          <Box display="flex" gap={1} alignItems="center">
            <VerificationRequestStatusBar
              status={verificationRequest?.status}
              isMyVerifyRequest={verificationRequest?.isMyVerifyRequest}
            />
            <InboxItemPlatformStaffMenu inboxItemId={inboxItemId} />
          </Box>
        )
      }
      stripePaymentStatus={
        <InboxItemContentVerificationRequestPaymentStatus inboxItemId={inboxItemId} />
      }
      activityLog={
        <React.Activity
          mode={inboxItem?.type !== InboxItemTypeWithResources.Resources ? 'visible' : 'hidden'}
        >
          <InboxItemContentVerificationRequestCreateCommentForm />
          <ActivityLogsList inboxItemId={inboxItemId} />
        </React.Activity>
      }
    >
      {inboxItemContent}
    </InboxItemContentWrapper>
  );
};
